import api from './api';

export type LeadSourceAuthMode = 'path_token' | 'header_key';
export type LeadSourceConnectionStatus = 'active' | 'paused' | 'archived';

export interface ColumnMapping {
  header: string;
  crmField: string;
  confidence: number;
}

export interface LeadSourceCredentialSafe {
  id: string;
  keyId: string;
  authMode: LeadSourceAuthMode;
  label: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  expiresAt: string | null;
  supersededBy: string | null;
  createdAt: string;
}

export interface LeadSourceConnection {
  id: string;
  organizationId: string;
  providerKey: string;
  name: string;
  status: LeadSourceConnectionStatus;
  leadSource: string;
  fieldMapping: ColumnMapping[];
  defaultTags: string[];
  rateLimitPerMinute: number;
  lastSubmissionAt: string | null;
  lastErrorAt: string | null;
  lastErrorCode: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  credentials: LeadSourceCredentialSafe[];
}

export interface IssuedCredential {
  keyId: string;
  plaintext: string;
}

export interface CreateLeadSourceConnectionPayload {
  providerKey: string;
  name: string;
  leadSource: string;
  authMode: LeadSourceAuthMode;
  fieldMapping?: ColumnMapping[];
  defaultTags?: string[];
  rateLimitPerMinute?: number;
}

export interface UpdateLeadSourceConnectionPayload {
  name?: string;
  fieldMapping?: ColumnMapping[];
  defaultTags?: string[];
  rateLimitPerMinute?: number;
  status?: LeadSourceConnectionStatus;
}

export interface CreateLeadSourceConnectionResponse {
  connection: LeadSourceConnection;
  credential: IssuedCredential;
}

export interface LeadSourceCatalogProvider {
  providerKey: string;
  label: string;
  description?: string;
}

export const leadSourcesAPI = {
  list: (organizationId: string) =>
    api.get<LeadSourceConnection[]>(`/organizations/${organizationId}/lead-sources`),

  getOne: (organizationId: string, id: string) =>
    api.get<LeadSourceConnection>(`/organizations/${organizationId}/lead-sources/${id}`),

  create: (organizationId: string, payload: CreateLeadSourceConnectionPayload) =>
    api.post<CreateLeadSourceConnectionResponse>(
      `/organizations/${organizationId}/lead-sources`,
      payload,
    ),

  update: (organizationId: string, id: string, payload: UpdateLeadSourceConnectionPayload) =>
    api.patch<LeadSourceConnection>(`/organizations/${organizationId}/lead-sources/${id}`, payload),

  rotateCredential: (organizationId: string, id: string) =>
    api.post<IssuedCredential>(
      `/organizations/${organizationId}/lead-sources/${id}/credentials/rotate`,
    ),

  revoke: (organizationId: string, id: string) =>
    api.post<void>(`/organizations/${organizationId}/lead-sources/${id}/revoke`),

  remove: (organizationId: string, id: string) =>
    api.delete<void>(`/organizations/${organizationId}/lead-sources/${id}`),

  // Bounded retry and error surfacing for transient failures on this call
  // live in useLeadSourceCatalog, not here.
  catalog: () => api.get<LeadSourceCatalogProvider[]>('/lead-sources/catalog'),
};
